import React, { useEffect, useState, useMemo } from "react";
import { Trash2, Edit, Search } from "lucide-react";
import axios from "axios";
import Apiurl from "../../environment/Apiurl";

function Holiday() {
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [activePage, setActivePage] = useState(1);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);

  // Form state for adding
  const [addData, setAddData] = useState({
    date: "",
    holiday_name: "",
  });

  // Form state for editing
  const [updateData, setUpdateData] = useState({
    date: "",
    holiday_name: "",
  });

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // Fetch user privileges based on role_id
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);

      if (userRoleId === "unknown") {
        console.warn("No role_id found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setLoadingPrivileges(false);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data);

      const roleData = response.data.find(
        (role) => role.role_id === userRoleId
      );

      if (roleData) {
        console.log("Found role data for role_id:", userRoleId, roleData);
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page.toLowerCase();
          privileges[pageName] = {
            access: page.access,
            ...page.priviallages,
          };
        });
        console.log("Set user privileges:", privileges);
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
      }
    } catch (error) {
      console.error("Error fetching user privileges:", error.message);
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Holiday page
  const hasPermission = (permission) => {
    const holidayPrivileges =
      userPrivileges["holiday"] || userPrivileges["Holiday"];

    if (!holidayPrivileges) {
      console.log(
        `No privileges found for Holiday page (checked: holiday, Holiday)`
      );
      return false;
    }

    console.log(
      `Checking permission '${permission}' for Holiday:`,
      holidayPrivileges
    );

    if (permission === "access") {
      const accessGranted = holidayPrivileges.access === true;
      console.log(`Permission 'access' result:`, accessGranted);
      return accessGranted;
    }

    if (
      permission in holidayPrivileges &&
      holidayPrivileges[permission] === false
    ) {
      console.log(`Permission '${permission}' explicitly denied`);
      return false;
    }

    const hasPerm =
      holidayPrivileges.access &&
      (holidayPrivileges.all || holidayPrivileges[permission]);
    console.log(`Permission '${permission}' result:`, hasPerm);
    return hasPerm;
  };

  // Fetch data from API
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Holiday page");
      setError("You do not have permission to view holidays.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/holiday`);
      console.log("Holidays data fetched:", response.data);
      setResponseData(response.data.reverse() || []);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching holidays:",
        error.message,
        error.response?.data
      );
      setError("Failed to fetch holidays. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes for add form
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Add form input changed: ${name} = ${value}`);
    setAddData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle input changes for edit form
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Edit form input changed: ${name} = ${value}`);
    setUpdateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validate form data
  const validateForm = (data) => {
    if (!data.date) return "Date is required.";
    if (!data.holiday_name) return "Holiday name is required.";
    return null;
  };

  // Handle add form submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Holiday page");
      setError("You do not have permission to add holidays.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(addData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const formData = {
        date: addData.date,
        holiday_name: addData.holiday_name,
      };
      console.log("Adding holiday with data:", formData);
      await axios.post(`${Apiurl}/holiday`, formData);
      setAddData({
        date: "",
        holiday_name: "",
      });
      fetchData();
      closeAddModal();
    } catch (error) {
      console.error(
        "Error adding holiday:",
        error.message,
        error.response?.data
      );
      setError("Failed to add holiday. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

    useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Holiday page");
      setError("You do not have permission to edit holidays.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!editingId) {
      console.warn("Invalid holiday ID:", editingId);
      setError("Invalid holiday ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(updateData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Updating holiday with ID:", editingId, "Data:", updateData);
      await axios.put(`${Apiurl}/holiday/${editingId}`, updateData);
      fetchData();
      closeEditModal();
    } catch (error) {
      console.error(
        "Error updating holiday:",
        error.message,
        error.response?.data
      );
      setError("Failed to update holiday. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Reset add form
  const resetAddForm = () => {
    console.log("Resetting add form");
    setAddData({
      date: "",
      holiday_name: "",
    });
  };

  // Edit holiday
  const handleEdit = (id, holiday) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Holiday page");
      setError("You do not have permission to edit holidays.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid holiday ID:", id);
      setError("Invalid holiday ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Edit Holiday modal for ID:", id, "Holiday:", holiday);
    setEditingId(id);
    setUpdateData({
      date: holiday.date || "",
      holiday_name: holiday.holiday_name || "",
    });
    setShowEditModal(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Holiday page");
      setError("You do not have permission to delete holidays.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid holiday ID:", id);
      setError("Invalid holiday ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Holiday modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Holiday page");
      setError("You do not have permission to delete holidays.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Closing Delete Holiday modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  // Handle delete holiday
  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Holiday page");
      setError("You do not have permission to delete holidays.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid holiday ID:", deleteId);
      setError("Invalid holiday ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Deleting holiday with ID:", deleteId);
      await axios.delete(`${Apiurl}/holiday/${deleteId}`);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting holiday:",
        error.message,
        error.response?.data
      );
      setError("Failed to delete holiday. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Holiday page");
      setError("You do not have permission to add holidays.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Add Holiday modal");
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Holiday page");
      setError("You do not have permission to add holidays.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Closing Add Holiday modal");
    setShowAddModal(false);
    resetAddForm();
    setError(null);
  };

  const closeEditModal = () => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Holiday page");
      setError("You do not have permission to edit holidays.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Closing Edit Holiday modal");
    setShowEditModal(false);
    setEditingId(null);
    setUpdateData({
      date: "",
      holiday_name: "",
    });
    setError(null);
  };

  // Fetch privileges and data
  useEffect(() => {
    console.log("Fetching initial privileges...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    if (hasPermission("access")) {
      console.log("Access granted for Holiday page");
      if (hasPermission("read")) {
        console.log("Fetching holidays data due to read permission");
        fetchData();
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Holiday page");
      setError("You do not have permission to access this page.");
    }
  }, [userPrivileges, loadingPrivileges]);

  // Filter data with useMemo
  const filteredData = useMemo(() => {
    return responseData.filter(
      (holiday) =>
        (holiday.date?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          holiday.holiday_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())) ??
        false
    );
  }, [responseData, searchTerm]);

  // Update items per page when showEntries changes
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
    setCurrentPage(1);
    setActivePage(1);
  }, [showEntries, filteredData]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (activePage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (activePage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          activePage - 1,
          activePage,
          activePage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== activePage) {
      console.log("Navigating to page:", page);
      setActivePage(page);
      setCurrentPage(page);
    }
  };

  if (loadingPrivileges) {
    return <div className="text-center py-5">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Holiday page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      {hasPermission("create") && (
        <div className="text-end">
          <button
            className="btn btn-sm text-white"
            style={{ backgroundColor: "#01AEBE" }}
            onClick={openAddModal}
          >
            + Add Holiday
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="bg-white rounded shadow-sm">
            <div className="row mb-3 crm-table-toolbar">
              <div className="col-md-2">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-table"></i>
                  </span>
                  <select
                    className="form-select"
                    value={showEntries}
                    onChange={(e) => {
                      console.log(
                        "Changing items per page to:",
                        e.target.value
                      );
                      setShowEntries(e.target.value);
                    }}
                  >
                    <option value="All">Show All</option>
                    {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                      <option key={n} value={n}>
                        Show {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-7"></div>
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control pe-5"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => {
                        console.log("Search term changed to:", e.target.value);
                        setSearchTerm(e.target.value);
                      }}
                      style={{ width: "250px", paddingRight: "2.5rem" }}
                    />
                    <Search
                      size={16}
                      className="position-absolute text-muted"
                      style={{
                        right: "10px",
                        top: "35%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading && <div className="text-center py-3">Loading...</div>}

            <div className="table-responsive">
              <table className="table responsive-table">
                <thead className="table-light">
                  <tr style={{ backgroundColor: "#01AEBE", color: "white" }}>
                    <th>S.No</th>
                    <th>Date</th>
                    <th>Holiday Name</th>
                    {(hasPermission("update") || hasPermission("delete")) && (
                      <th>Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((holiday, index) => (
                      <tr key={holiday._id || index} className="align-middle">
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{holiday.date || "-"}</td>
                        <td>{holiday.holiday_name || "-"}</td>
                        {(hasPermission("update") ||
                          hasPermission("delete")) && (
                          <td>
                            {hasPermission("delete") && (
                              <button
                                className="btn btn-sm p-1 border-0"
                                style={{ backgroundColor: "transparent" }}
                                onClick={() => openDeleteModal(holiday._id)}
                              >
                                <Trash2 size={16} className="text-danger" />
                              </button>
                            )}
                            {hasPermission("update") && (
                              <button
                                className="btn btn-sm p-1 border-0"
                                style={{ backgroundColor: "transparent" }}
                                onClick={() => handleEdit(holiday._id, holiday)}
                              >
                                <Edit size={16} className="text-primary" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          hasPermission("update") || hasPermission("delete")
                            ? 4
                            : 3
                        }
                        className="text-center py-3"
                      >
                        {loading ? "Loading..." : "No holidays found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredData.length > 0 && (
              <div className="container">
                <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      activePage > 1 && handlePageClick(activePage - 1)
                    }
                    disabled={activePage === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                    <span className="d-none d-sm-inline">Previous</span>
                  </button>

                  <div
                    className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                    style={{ maxWidth: "100%" }}
                  >
                    {pages.map((page, index) => (
                      <button
                        key={index}
                        className={`btn btn-sm px-3 flex-shrink-0 ${
                          page === activePage
                            ? "text-white"
                            : "btn-outline-secondary"
                        }`}
                        style={
                          page === activePage
                            ? { backgroundColor: "#20c997", border: "none" }
                            : {}
                        }
                        onClick={() => handlePageClick(page)}
                        disabled={page === "..."}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      activePage < totalPages && handlePageClick(activePage + 1)
                    }
                    disabled={activePage === totalPages}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Holiday Modal */}
      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addHolidayModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title text-dark" id="addHolidayModalLabel">
                  Add Holiday
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="date" className="form-label fw-bold">
                          Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="date"
                          name="date"
                          value={addData.date}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="holiday_name"
                          className="form-label fw-bold"
                        >
                          Holiday Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="holiday_name"
                          name="holiday_name"
                          placeholder="Enter Holiday Name"
                          value={addData.holiday_name}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeAddModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white btn-sm px-4"
                      style={{ backgroundColor: "#01AEBE" }}
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Holiday Modal */}
      {hasPermission("update") && (
        <div
          className={`modal fade ${showEditModal ? "show" : ""}`}
          style={{ display: showEditModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="editHolidayModalLabel"
          aria-hidden={!showEditModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6
                  className="modal-title text-dark"
                  id="editHolidayModalLabel"
                >
                  Edit Holiday
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="date" className="form-label fw-bold">
                          Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="date"
                          name="date"
                          value={updateData.date}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="holiday_name"
                          className="form-label fw-bold"
                        >
                          Holiday Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="holiday_name"
                          name="holiday_name"
                          placeholder="Enter Holiday Name"
                          value={updateData.holiday_name}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeEditModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white btn-sm px-4"
                      style={{ backgroundColor: "#01AEBE" }}
                    >
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteHolidayModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deleteHolidayModalLabel">
                  Delete Holiday
                </h5>
                <p style={{ marginTop: "10px" }}>
                  You're going to delete this holiday. Are you sure?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                >
                  Yes, Delete It!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || showEditModal || showDeleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default Holiday;
